import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, Heart } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { formatCurrency } from "@/hooks/useGivingTotals";

type Chapter = "washington" | "union" | "all";

interface GivingEvent {
  id: string;
  chapter: Chapter;
  date: string;
  beneficiary: string;
  amount: number;
  notes: string | null;
  created_at: string;
}

const schema = z.object({
  chapter: z.enum(["washington", "union", "all"]),
  date: z.string().min(1, "Date is required"),
  beneficiary: z.string().trim().min(1, "Required").max(200),
  amount: z.coerce.number().nonnegative("Must be 0 or more"),
  notes: z.string().trim().max(2000).optional().or(z.literal("")),
});

const emptyForm = {
  chapter: "all" as Chapter,
  date: new Date().toISOString().split("T")[0],
  beneficiary: "",
  amount: "" as string | number,
  notes: "",
};

const AdminGivingList = () => {
  const [entries, setEntries] = useState<GivingEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<GivingEvent | null>(null);
  const [form, setForm] = useState<typeof emptyForm>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [filterChapter, setFilterChapter] = useState<"all-chapters" | Chapter>("all-chapters");
  const [filterYear, setFilterYear] = useState<string>("all");

  const fetchEntries = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("giving_events")
      .select("*")
      .order("date", { ascending: false });
    if (error) {
      toast.error("Failed to load giving entries");
    } else {
      setEntries((data as GivingEvent[]) || []);
    }
    setLoading(false);
  };

  useEffect(() => { fetchEntries(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setErrors({});
    setDialogOpen(true);
  };

  const openEdit = (entry: GivingEvent) => {
    setEditing(entry);
    setForm({
      chapter: entry.chapter,
      date: entry.date,
      beneficiary: entry.beneficiary,
      amount: entry.amount,
      notes: entry.notes || "",
    });
    setErrors({});
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const fe: Record<string, string> = {};
      for (const issue of parsed.error.issues) fe[issue.path[0] as string] = issue.message;
      setErrors(fe);
      return;
    }
    setSaving(true);
    const payload = {
      chapter: parsed.data.chapter,
      date: parsed.data.date,
      beneficiary: parsed.data.beneficiary,
      amount: parsed.data.amount,
      notes: parsed.data.notes || null,
    };
    const { error } = editing
      ? await supabase.from("giving_events").update(payload).eq("id", editing.id)
      : await supabase.from("giving_events").insert(payload);
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(editing ? "Entry updated" : "Entry added");
    setDialogOpen(false);
    fetchEntries();
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this giving entry? This cannot be undone.")) return;
    const { error } = await supabase.from("giving_events").delete().eq("id", id);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Entry deleted");
    fetchEntries();
  };

  const filtered = entries.filter((e) => {
    if (filterChapter !== "all-chapters" && e.chapter !== filterChapter) return false;
    if (filterYear !== "all" && !e.date.startsWith(filterYear)) return false;
    return true;
  });

  const total = filtered.reduce((sum, e) => sum + Number(e.amount), 0);
  const washingtonTotal = entries
    .filter((e) => e.chapter === "washington" || e.chapter === "all")
    .reduce((s, e) => s + Number(e.amount), 0);
  const unionTotal = entries
    .filter((e) => e.chapter === "union" || e.chapter === "all")
    .reduce((s, e) => s + Number(e.amount), 0);
  const grandTotal = entries.reduce((s, e) => s + Number(e.amount), 0);

  const years = Array.from(new Set(entries.map((e) => e.date.slice(0, 4)))).sort().reverse();
  const chapterLabel = (c: Chapter) =>
    c === "washington" ? "Washington" : c === "union" ? "Union" : "All chapters";

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SummaryCard label="Grand Total" value={formatCurrency(grandTotal)} />
        <SummaryCard label="Washington Chapter" value={formatCurrency(washingtonTotal)} />
        <SummaryCard label="Union Chapter" value={formatCurrency(unionTotal)} />
      </div>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Heart className="h-5 w-5 text-primary" />
              Giving Entries
            </CardTitle>
            <CardDescription>
              Track charitable giving and community contributions. The grand total appears publicly on the Impact page.
            </CardDescription>
          </div>
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Add Entry
          </Button>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <Select value={filterChapter} onValueChange={(v) => setFilterChapter(v as any)}>
              <SelectTrigger className="sm:w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all-chapters">All chapters</SelectItem>
                <SelectItem value="washington">Washington</SelectItem>
                <SelectItem value="union">Union</SelectItem>
                <SelectItem value="all">Joint (all)</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterYear} onValueChange={setFilterYear}>
              <SelectTrigger className="sm:w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All years</SelectItem>
                {years.map((y) => (
                  <SelectItem key={y} value={y}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="sm:ml-auto text-sm text-muted-foreground self-center">
              Filtered total: <span className="font-semibold text-foreground">{formatCurrency(total)}</span>
              {" "}({filtered.length} {filtered.length === 1 ? "entry" : "entries"})
            </div>
          </div>

          {loading ? (
            <p className="text-center text-muted-foreground py-8">Loading...</p>
          ) : filtered.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No giving entries yet. Click "Add Entry" to record your first one.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Chapter</TableHead>
                    <TableHead>Beneficiary</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="whitespace-nowrap">
                        {new Date(e.date + "T00:00:00").toLocaleDateString()}
                      </TableCell>
                      <TableCell>{chapterLabel(e.chapter)}</TableCell>
                      <TableCell className="font-medium">{e.beneficiary}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {formatCurrency(Number(e.amount))}
                      </TableCell>
                      <TableCell className="max-w-xs truncate text-muted-foreground">
                        {e.notes}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openEdit(e)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDelete(e.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit giving entry" : "Add giving entry"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                />
                {errors.date && <p className="text-sm text-destructive">{errors.date}</p>}
              </div>
              <div className="space-y-2">
                <Label>Chapter</Label>
                <Select
                  value={form.chapter}
                  onValueChange={(v) => setForm((f) => ({ ...f, chapter: v as Chapter }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Joint (all chapters)</SelectItem>
                    <SelectItem value="washington">Washington</SelectItem>
                    <SelectItem value="union">Union</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Beneficiary</Label>
              <Input
                value={form.beneficiary}
                onChange={(e) => setForm((f) => ({ ...f, beneficiary: e.target.value }))}
                placeholder="e.g., Franklin County Foster Closet"
              />
              {errors.beneficiary && <p className="text-sm text-destructive">{errors.beneficiary}</p>}
            </div>
            <div className="space-y-2">
              <Label>Amount (USD)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={form.amount}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                placeholder="0.00"
              />
              {errors.amount && <p className="text-sm text-destructive">{errors.amount}</p>}
            </div>
            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                rows={3}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Event name, organizer, context..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : editing ? "Save changes" : "Add entry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SummaryCard = ({ label, value }: { label: string; value: string }) => (
  <Card>
    <CardContent className="pt-6">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="text-3xl font-bold text-primary mt-1">{value}</p>
    </CardContent>
  </Card>
);

export default AdminGivingList;
